import { useContext, useState } from "react";
import Link from "next/link";

import useMediaQuery from "@vahak/core-ui/dist/hooks/useMediaQuery";
import { ROUTES } from "@vahak/core/dist/constants/routeStrings";
import { GA4EventNames } from "@vahak/core/dist/constants/event-names";

import ArrowRightGrey from "@vahak/core/dist/icons/ArrowRightGrey.svg";
import Styles from "./AvailableLoadsSectionV2.module.scss";
import useHomepageV2Translation from "../hooks/useHomepageV2Translation";
import HomepageV2SectionCard from "../section-card/HomepageV2SectionCard";
import HomepageV2Animate from "../animate/HomepageV2Animate";
import { LoadMarketCard } from "../../../services/load/useGetLoadMarketV2/type";
import { useGetLoadMarketInfiniteQueryNew } from "../../../services/load/useGetLoadMarketV2/useGetLoadMarketInfiniteQueryNew";
import { IEventTrackerProp } from "../../../types/events";
import AvailableLoadsGrid from "./AvailableLoadsGrid";
import { AppContext } from "@vahak/core/dist/app-context";

type AvailableLoadsSectionV2Props = IEventTrackerProp & {
    onBidNow: (load: LoadMarketCard, rank: number) => void;
};

const AvailableLoadsSectionV2 = ({ trackEvent, eventData, onBidNow }: AvailableLoadsSectionV2Props) => {
    const { t } = useHomepageV2Translation();
    const isMobileScreen = useMediaQuery({ queryType: "mobile" });
    const maxCards = isMobileScreen ? 2 : 6;
    const [loadsData, setLoadsData] = useState<LoadMarketCard[]>([]);
    const { companyId } = useContext(AppContext);

    const { isFetched, isFetching } = useGetLoadMarketInfiniteQueryNew(
        {
            preloads: ["company_detail", "relevancy_tags"],
            l: maxCards,
            c_i: Number(companyId) || undefined
        },
        {
            onSuccess(data) {
                setLoadsData(data[0]?.load_cards ?? []);
            },
            onError() {}
        }
    );

    const visibleLoads = loadsData.slice(0, maxCards);
    const isLoading = !isFetched || (isFetching && visibleLoads.length === 0);

    if (!isLoading && visibleLoads.length === 0) {
        return null;
    }

    return (
        <HomepageV2SectionCard variant="light">
            <div className={Styles.container}>
                <div className={Styles.header}>
                    <HomepageV2Animate variant="fade-right">
                        <p className={Styles.eyebrow}>{t("homepageV2:availableLoads.eyebrow")}</p>
                        <h2 className={Styles.title}>
                            <span className={Styles.titleLine}>{t("homepageV2:availableLoads.title")}</span>
                            <span className={Styles.titleHighlight}>
                                {t("homepageV2:availableLoads.titleHighlight")}
                            </span>
                        </h2>
                    </HomepageV2Animate>
                </div>

                <AvailableLoadsGrid
                    loads={visibleLoads}
                    cardIdPrefix="homepage-v2"
                    onBidNow={onBidNow}
                    isLoading={isLoading}
                    shimmerCount={maxCards}
                />

                {!isLoading ? (
                    <HomepageV2Animate variant="fade-up" delay={180}>
                        <div className={Styles.viewMoreWrap}>
                            <Link
                                href={ROUTES.marketplace.getUrl("load")}
                                prefetch={false}
                                className={Styles.viewMoreBtn}
                                onClick={() =>
                                    trackEvent?.(GA4EventNames.WEB_V2.view_more_loads_clicked, eventData)
                                }
                            >
                                {t("homepageV2:availableLoads.viewMore")}
                                <ArrowRightGrey />
                            </Link>
                        </div>
                    </HomepageV2Animate>
                ) : null}
            </div>
        </HomepageV2SectionCard>
    );
};

export default AvailableLoadsSectionV2;
